import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  Checkbox,
  TextField,
  Typography,
  InputAdornment,
  FormControlLabel,
  Skeleton,
} from "@mui/material";
import { WatchLater } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";

import { feedback } from "../../shared/services";
import {
  EstablishmentService,
  IEstablishmentConfig,
  IEstablishmentConfigFull,
} from "../../shared/services/api/EstablishmentService";
import { Footer } from "../../shared/components/Footer";
import { RichText } from "../../shared/components/RichText";
import { checkValidUUID } from "../../shared/utils/checkValidUUID";

export const EstablishmentConfig: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [methodType, setMethodType] = useState<"post" | "put">("post");
  const [config, setConfig] = useState<IEstablishmentConfigFull>({
    idEstabelecimento: "",
    nomeEstabelecimento: "",
    configuracao: {
      duracaoDoToken: 60,
      instrucaoDeUsoDaAssinatura: "",
      termoDeAceite: {
        usaTermo: false,
        template: "",
      },
      emailDeVerificacao: {
        remetenteConta: "",
        assunto: "",
        template: "",
      },
    },
  });

  const navigate = useNavigate();
  const params = useParams<"id">();

  const checkAcceptTerm = useMemo(
    () =>
      (!config.configuracao.termoDeAceite.template ||
        config.configuracao.termoDeAceite.template ===
          "<p><span></span></p>") &&
      submitted,
    [config.configuracao.termoDeAceite.template, submitted]
  );

  const checkInstructionForUse = useMemo(
    () =>
      (!config.configuracao.instrucaoDeUsoDaAssinatura ||
        config.configuracao.instrucaoDeUsoDaAssinatura ===
          "<p><span></span></p>") &&
      submitted,
    [config.configuracao.instrucaoDeUsoDaAssinatura, submitted]
  );

  const checkVerificationEmail = useMemo(
    () =>
      (!config.configuracao.emailDeVerificacao.template ||
        config.configuracao.emailDeVerificacao.template ===
          "<p><span></span></p>") &&
      submitted,
    [config.configuracao.emailDeVerificacao.template, submitted]
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await EstablishmentService.getEstablishmentConfigs(
        params.id.split("_")[0]
      );

      if (!response.configuracao) {
        setMethodType("post");
        setConfig({
          ...response,
          configuracao: {
            idEstabelecimento: response.idEstabelecimento,
            duracaoDoToken: 60,
            instrucaoDeUsoDaAssinatura: "",
            termoDeAceite: {
              usaTermo: false,
              template: "",
            },
            emailDeVerificacao: {
              remetenteConta: "",
              assunto: "",
              template: "",
            },
          },
        });
      } else {
        setMethodType("put");
        setConfig({
          ...response,
          configuracao: {
            ...response.configuracao,
            id: params.id.split("_")[1],
          },
        });
      }
    } catch (error) {
      feedback(error, "error");
    } finally {
      setLoading(false);
    }
  }, [config, params]);

  const handleSaveConfig = useCallback(async () => {
    if (
      config.configuracao.duracaoDoToken <= 0 ||
      config.configuracao.emailDeVerificacao.assunto === "" ||
      config.configuracao.emailDeVerificacao.remetenteConta === ""
    ) {
      setSubmitted(true);
      return;
    } else {
      setLoading(true);
      try {
        methodType === "post"
          ? await EstablishmentService.postEstablishmentConfigs(
              config.configuracao
            )
          : await EstablishmentService.putEstablishmentConfigs(
              params.id.split("_")[1],
              config.configuracao
            );

        feedback(
          "Configuração de estabelecimento salva com sucesso",
          "success"
        );

        navigate(`/estabelecimentos`);
      } catch (error) {
        feedback(error, "error");
      } finally {
        setLoading(false);
        setSubmitted(false);
      }
    }
  }, [config]);

  useEffect(() => {
    loadConfig();
  }, []);

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 10 }}>
        <Grid item xs={12} mb={3}>
          <Typography variant="h5" color="primary">
            {loading ? (
              <Skeleton width={450} />
            ) : (
              <strong>{`Estabelecimento: ${config.idEstabelecimento} - ${config.nomeEstabelecimento}`}</strong>
            )}
          </Typography>
        </Grid>

        <Grid item xs={12} mb={2}>
          {loading ? (
            <Skeleton variant="text" width={350} />
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  disabled={loading}
                  checked={config.configuracao.termoDeAceite.usaTermo}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setConfig(
                      (state) =>
                        (state = {
                          ...state,
                          configuracao: {
                            ...state.configuracao,
                            termoDeAceite: {
                              ...state.configuracao.termoDeAceite,
                              usaTermo: event.target.checked,
                            },
                          },
                        })
                    );
                  }}
                />
              }
              label="Requer termo de utilização da assinatura eletrônica"
            />
          )}
        </Grid>

        <Grid item xs={12} mb={1}>
          <Typography variant="subtitle1">
            <strong>
              Termo de utilização do token de assinatura eletrônica*
            </strong>
          </Typography>
        </Grid>

        <Grid item xs={7} mb={3}>
          {loading ? (
            <Skeleton variant="rectangular" width={550} height={140} />
          ) : (
            <RichText
              disabled={!config.configuracao.termoDeAceite.usaTermo || loading}
              macroType="Terms"
              error={checkAcceptTerm}
              value={config.configuracao.termoDeAceite.template}
              helperText={checkAcceptTerm ? "Campo obrigatório" : undefined}
              setValue={(value) =>
                setConfig(
                  (state) =>
                    (state = {
                      ...state,
                      configuracao: {
                        ...state.configuracao,
                        termoDeAceite: {
                          ...state.configuracao.termoDeAceite,
                          template: value,
                        },
                      },
                    })
                )
              }
            />
          )}
        </Grid>

        <Grid item xs={12} mb={1}>
          <Typography variant="subtitle1">
            Tempo de duração do token de assinatura eletrônica*
          </Typography>
        </Grid>

        <Grid item xs={2} mb={2}>
          {loading ? (
            <Skeleton variant="text" width={200} height={60} />
          ) : (
            <TextField
              fullWidth
              type="number"
              label="Minutos"
              variant="outlined"
              value={config.configuracao.duracaoDoToken}
              error={!config.configuracao.duracaoDoToken && submitted}
              helperText={
                !config.configuracao.duracaoDoToken &&
                submitted &&
                "Campo obrigatório"
              }
              onChange={({ target }) =>
                setConfig(
                  (state) =>
                    (state = {
                      ...state,
                      configuracao: {
                        ...state.configuracao,
                        duracaoDoToken: Number(target.value),
                      },
                    })
                )
              }
              InputProps={{
                inputProps: { min: 1 },
                endAdornment: (
                  <InputAdornment position="end">
                    <WatchLater />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Grid>

        <Grid item xs={12} mb={1}>
          <Typography variant="subtitle1">
            <strong>Orientações para aplicar a assinatura eletrônica*</strong>
          </Typography>
        </Grid>

        <Grid item xs={7} mb={3}>
          {loading ? (
            <Skeleton variant="rectangular" width={550} height={140} />
          ) : (
            <RichText
              disabled={loading}
              macroType="Orientation"
              error={checkInstructionForUse}
              value={config.configuracao.instrucaoDeUsoDaAssinatura}
              helperText={
                checkInstructionForUse ? "Campo obrigatório" : undefined
              }
              setValue={(value) =>
                setConfig(
                  (state) =>
                    (state = {
                      ...state,
                      configuracao: {
                        ...state.configuracao,
                        instrucaoDeUsoDaAssinatura: value,
                      },
                    })
                )
              }
            />
          )}
        </Grid>

        <Grid item xs={12} mb={1}>
          <Typography variant="subtitle1">
            <strong>E-mail para assinatura eletrônica*</strong>
          </Typography>
        </Grid>

        <Grid item xs={7} mb={2}>
          {loading ? (
            <Skeleton variant="text" width={550} height={60} />
          ) : (
            <TextField
              fullWidth
              label="Assunto"
              variant="outlined"
              disabled={loading}
              value={config.configuracao.emailDeVerificacao.assunto}
              error={
                !config.configuracao.emailDeVerificacao.assunto && submitted
              }
              helperText={
                !config.configuracao.emailDeVerificacao.assunto &&
                submitted &&
                "Campo obrigatório"
              }
              onChange={({ target }) =>
                setConfig(
                  (state) =>
                    (state = {
                      ...state,
                      configuracao: {
                        ...state.configuracao,
                        emailDeVerificacao: {
                          ...state.configuracao.emailDeVerificacao,
                          assunto: target.value,
                        },
                      },
                    })
                )
              }
            />
          )}
        </Grid>
        <Grid item xs={5} />

        <Grid item xs={7} mb={3}>
          {loading ? (
            <Skeleton variant="rectangular" width={550} height={140} />
          ) : (
            <RichText
              macroType="Email"
              disabled={loading}
              error={checkVerificationEmail}
              value={config.configuracao.emailDeVerificacao.template}
              helperText={
                checkVerificationEmail ? "Campo obrigatório" : undefined
              }
              setValue={(value) =>
                setConfig(
                  (state) =>
                    (state = {
                      ...state,
                      configuracao: {
                        ...state.configuracao,
                        emailDeVerificacao: {
                          ...state.configuracao.emailDeVerificacao,
                          template: value,
                        },
                      },
                    })
                )
              }
            />
          )}
        </Grid>

        <Grid item xs={7}>
          {loading ? (
            <Skeleton variant="text" width={550} height={60} />
          ) : (
            <TextField
              fullWidth
              variant="outlined"
              disabled={loading}
              label="Conta de E-mail do remetente*"
              value={config.configuracao.emailDeVerificacao.remetenteConta}
              error={
                !config.configuracao.emailDeVerificacao.remetenteConta &&
                submitted
              }
              helperText={
                !config.configuracao.emailDeVerificacao.remetenteConta &&
                submitted &&
                "Campo obrigatório"
              }
              onChange={({ target }) =>
                setConfig(
                  (state) =>
                    (state = {
                      ...state,
                      configuracao: {
                        ...state.configuracao,
                        emailDeVerificacao: {
                          ...state.configuracao.emailDeVerificacao,
                          remetenteConta: target.value,
                        },
                      },
                    })
                )
              }
            />
          )}
        </Grid>
      </Grid>

      <Footer
        disabled={loading}
        onCancel={() => navigate(`/estabelecimentos`)}
        onSave={() => handleSaveConfig()}
      />
    </>
  );
};
